/**
 * Generic display-unit selection from provider placement configuration.
 * Unit IDs and device_class come from the API payload (DB) — never hard-coded here.
 *
 * When the API already picked a unit for this placement (e.g. distinct shop
 * sidebar slots), preserve that provider_unit_id if it is valid for the
 * current device. Only fall back to the first device-matched unit when the
 * payload has no usable selection or the viewport device class changed.
 */

export function detectDeviceClass() {
  if (typeof window === "undefined") return "desktop";
  return window.innerWidth < 768 ? "mobile" : "desktop";
}

/**
 * @param {object} network
 * @param {object} context  { device: 'desktop'|'mobile' }
 * @returns {object}
 */
export function selectConfiguredUnit(network, context) {
  if (!network) return network;
  var device =
    context && context.device
      ? String(context.device)
      : detectDeviceClass();
  if (device === "phone" || device === "tablet") {
    device = "mobile";
  }
  if (device !== "mobile") {
    device = "desktop";
  }

  var units = network.units;
  if (!units || !units.length) {
    return Object.assign({}, network, { device_class: device });
  }

  var matches = [];
  var i;
  for (i = 0; i < units.length; i++) {
    var dc = units[i] && units[i].device_class ? String(units[i].device_class) : "all";
    if (dc === device || dc === "all") {
      matches.push(units[i]);
    }
  }
  if (!matches.length) {
    matches = units;
  }

  // Prefer the API-selected unit when it remains valid for this device.
  // Placement resolvers (shop sidebar slots, feed, banners) may already pick a
  // non-first unit; overwriting with matches[0] collapses distinct slots onto
  // the same provider_unit_id and breaks multi-instance CatholicAdnet fills.
  var preferredId =
    network.provider_unit_id != null && String(network.provider_unit_id) !== ""
      ? String(network.provider_unit_id)
      : "";
  var preferredNumericId =
    network.unit_id != null && network.unit_id !== ""
      ? String(network.unit_id)
      : "";
  var unit = matches[0];
  if (preferredId || preferredNumericId) {
    for (i = 0; i < matches.length; i++) {
      var candidate = matches[i];
      var candPid =
        candidate && candidate.provider_unit_id != null
          ? String(candidate.provider_unit_id)
          : "";
      var candId =
        candidate && candidate.id != null ? String(candidate.id) : "";
      if (
        (preferredId && candPid === preferredId) ||
        (preferredNumericId && candId === preferredNumericId)
      ) {
        unit = candidate;
        break;
      }
    }
  }

  var refresh = unit.refresh_seconds != null ? parseInt(unit.refresh_seconds, 10) : 0;
  if (!(refresh > 0) && network.refresh_seconds) {
    refresh = parseInt(network.refresh_seconds, 10) || 0;
  }

  return Object.assign({}, network, {
    unit_id: unit.id != null ? unit.id : network.unit_id,
    provider_unit_id: unit.provider_unit_id || network.provider_unit_id,
    width: unit.width != null ? unit.width : network.width,
    height: unit.height != null ? unit.height : network.height,
    refresh_seconds: refresh,
    device_class: device,
  });
}
